export type MetaAttribution =
  | {
      consent: true;
      capturedAt: number;
      sourceUrl: string;
      userAgent: string;
      fbp?: string;
      fbc?: string;
    }
  | { consent: false };

const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_FIELD_LENGTH = 500;
const META_BROWSER_ID = /^fb\.\d+\.\d{10,}\.[A-Za-z0-9._-]+$/;

export const metaAttributionCookieName = (hostname: string): string => {
  if (hostname.includes('staging')) return 'vst_meta_attribution_staging';
  if (hostname.includes('test')) return 'vst_meta_attribution_test';
  if (hostname === 'verisart.com' || hostname.endsWith('.verisart.com')) {
    return 'vst_meta_attribution';
  }
  return 'vst_meta_attribution_local';
};

const cookieValue = (cookies: string, name: string): string | undefined => {
  const pair = cookies
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair?.slice(name.length + 1);
};

const validField = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= MAX_FIELD_LENGTH;

const validBrowserId = (value: unknown): value is string =>
  validField(value) && META_BROWSER_ID.test(value);

export const parseMetaAttributionCookie = (
  hostname: string,
  cookies: string,
  now = Date.now()
): MetaAttribution | undefined => {
  const raw = cookieValue(cookies, metaAttributionCookieName(hostname));
  return parseMetaAttributionValue(raw, now);
};

const parseMetaAttributionValue = (
  raw: string | null | undefined,
  now = Date.now()
): MetaAttribution | undefined => {
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Record<
      string,
      unknown
    >;
    if (parsed.consent === false) return { consent: false };
    if (
      parsed.consent !== true ||
      typeof parsed.capturedAt !== 'number' ||
      !Number.isFinite(parsed.capturedAt) ||
      parsed.capturedAt > now ||
      now - parsed.capturedAt > MAX_AGE_MS ||
      !validField(parsed.sourceUrl) ||
      !validField(parsed.userAgent)
    ) {
      return undefined;
    }

    const fbp = validBrowserId(parsed.fbp) ? parsed.fbp : undefined;
    const fbc = validBrowserId(parsed.fbc) ? parsed.fbc : undefined;
    if (!fbp && !fbc) return undefined;

    return {
      consent: true,
      capturedAt: parsed.capturedAt,
      sourceUrl: parsed.sourceUrl,
      userAgent: parsed.userAgent,
      ...(fbp ? { fbp } : {}),
      ...(fbc ? { fbc } : {}),
    };
  } catch {
    return undefined;
  }
};

// Local fallbacks are for the development server, including the documented
// Verisart Shopify ngrok tunnel. Unknown hosts must not be treated as local.
const isDevelopmentAttributionHost = (hostname: string): boolean =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '[::1]' ||
  /^verisart-[a-z0-9-]+-shopify\.eu\.ngrok\.io$/.test(hostname);

/** Attribution is optional: blocked browser storage must never block billing or setup. */
export const readBrowserMetaAttribution = (
  hostname = window.location.hostname
): MetaAttribution | undefined => {
  try {
    const attribution = parseMetaAttributionCookie(hostname, document.cookie);
    if (attribution) return attribution;
  } catch {
    // Embedded browsers may deny even the cookie getter.
  }

  if (
    process.env.NODE_ENV !== 'development' ||
    !isDevelopmentAttributionHost(hostname)
  ) {
    return undefined;
  }

  try {
    const attribution = parseMetaAttributionStorage(
      hostname,
      window.localStorage
    );
    if (attribution) return attribution;
  } catch {
    // Both accessing localStorage and calling getItem can throw.
  }

  return localTestMetaAttribution(
    process.env.REACT_APP_META_TEST_CLICK_ID,
    window.navigator.userAgent
  );
};

export const parseMetaAttributionStorage = (
  hostname: string,
  storage: Pick<Storage, 'getItem'>,
  now = Date.now()
): MetaAttribution | undefined => {
  const name = metaAttributionCookieName(hostname);
  if (name !== 'vst_meta_attribution_local') return undefined;
  return parseMetaAttributionValue(storage.getItem(name), now);
};

export const localTestMetaAttribution = (
  clickId: string | undefined,
  userAgent: string,
  capturedAt = Date.now()
): MetaAttribution | undefined => {
  if (!clickId) return undefined;
  return parseMetaAttributionValue(
    JSON.stringify({
      consent: true,
      capturedAt,
      sourceUrl: `http://localhost:3000/application/landing/shopify?fbclid=${clickId}`,
      userAgent,
      fbp: `fb.1.${capturedAt}.1234567890`,
      fbc: `fb.1.${capturedAt}.${clickId}`,
    }),
    capturedAt
  );
};

export const setupShopPayload = <T extends Record<string, string>>(
  params: T,
  attribution?: MetaAttribution
): T | (T & { metaAttribution: MetaAttribution }) =>
  attribution ? { ...params, metaAttribution: attribution } : params;

export const changePlanPayload = <T extends Record<string, unknown>>(
  params: T,
  attribution?: MetaAttribution
): T | (T & { metaAttribution: MetaAttribution }) =>
  attribution ? { ...params, metaAttribution: attribution } : params;
